import React from 'react';
import { PlayerProfile, PlayerInjury, PlayerTransfer, PlayerAchievement, PlayerStats } from '../../types/player';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { cn } from '../../lib/utils';
import { HorizontalCareerTimeline } from './HorizontalCareerTimeline';

interface PlayerDetailsTabsProps {
  playerProfile: PlayerProfile;
  playerInjuries: PlayerInjury[] | null;
  playerTransfers: PlayerTransfer[] | null;
  playerAchievements: PlayerAchievement[] | null;
  playerStats: PlayerStats[] | null;
}

export const PlayerDetailsTabs: React.FC<PlayerDetailsTabsProps> = ({
  playerProfile,
  playerInjuries,
  playerTransfers,
  playerAchievements,
  playerStats,
}) => {
  return (
    <>
      {/* Injuries Section - Temporarily hidden */}
      {/*
      <div className="mt-6 pt-6 border-t border-gray-700">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="injuries" className="border-none">
            <AccordionTrigger className="py-2 px-4 hover:no-underline">
              <h4 className="text-xl font-bold text-[#CECFD2]">Injuries</h4>
            </AccordionTrigger>
            <AccordionContent>
              {!playerInjuries || playerInjuries.length === 0 ? (
                <p className="text-sm text-[#CECFD2] opacity-70">
                  No recorded injuries
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg shadow-lg bg-[#121212] p-4">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-[#CECFD2] border-b border-gray-700">
                        <th className="px-3 py-2">Season</th>
                        <th className="px-3 py-2">Injury</th>
                        <th className="px-3 py-2">From</th>
                        <th className="px-3 py-2">Until</th>
                        <th className="px-3 py-2">Days</th>
                        <th className="px-3 py-2">Games Missed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {playerInjuries.map((injury, index) => {
                        // Check if injury is active (no end date or end date is in the future)
                        const isActiveInjury = !injury.until_date || new Date(injury.until_date) > new Date();
                        
                        return (
                          <tr 
                            key={index} 
                            className={`text-sm hover:bg-[#26222E] transition-colors duration-150 ${
                              isActiveInjury ? 'bg-[#26222E]/40' : ''
                            }`}
                          >
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">{injury.season}</td>
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">
                              {injury.injury}
                              {isActiveInjury && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-[#26222E] text-[#4E46FF]">
                                  Active Injury
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">
                              {new Date(injury.from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </td>
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">
                              {injury.until_date 
                                ? new Date(injury.until_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                : <span className="text-[#4E46FF] font-semibold">Ongoing</span>
                              }
                            </td>
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">{injury.days}</td>
                            <td className="px-3 py-2 font-medium text-[#CECFD2]">{injury.games_missed}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      */}
      
      {/* Transfers Section - Temporarily hidden */}
      {/*
      <div className="mt-6 pt-6 border-t border-gray-700">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="transfers" className="border-none">
            <AccordionTrigger className="py-2 px-4 hover:no-underline">
              <h4 className="text-xl font-bold text-[#CECFD2]">Transfer History</h4>
            </AccordionTrigger>
            <AccordionContent>
              {!playerTransfers || playerTransfers.length === 0 ? (
                <p className="text-sm text-[#CECFD2] opacity-70">
                  No recorded transfers
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg shadow-lg bg-[#121212] p-4">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-[#CECFD2] border-b border-gray-700">
                        <th className="px-3 py-2">Season</th>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">From</th>
                        <th className="px-3 py-2">To</th>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Market Value</th>
                        <th className="px-3 py-2">Fee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {playerTransfers.map((transfer, index) => (
                        <tr 
                          key={index} 
                          className="text-sm hover:bg-[#26222E] transition-colors duration-150"
                        >
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{transfer.season}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">
                            {new Date(transfer.transfer_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{transfer.from_club_name}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{transfer.to_club_name}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">
                            <span className={`px-2 py-0.5 rounded-md text-xs ${
                              transfer.is_loan_end ? 'bg-[#26222E] text-[#CECFD2]' : 
                              transfer.is_loan ? 'bg-[#26222E] text-[#4E46FF]' : 
                              'bg-[#26222E] text-[#4E46FF]'
                            }`}>
                              {transfer.is_loan_end ? 'End of loan' : 
                               transfer.is_loan ? 'Loan' : 'Transfer'}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{transfer.market_value}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{transfer.fee}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      */}
      
      {/* Statistics Section */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="statistics" className="border-none">
            <AccordionTrigger className="py-2 px-4 hover:no-underline">
              <h4 className="text-xl font-bold text-[#CECFD2]">Statistics</h4>
            </AccordionTrigger>
            <AccordionContent>
              {!playerStats || !Array.isArray(playerStats) || playerStats.length === 0 ? (
                <p className="text-sm text-[#CECFD2] opacity-70">
                  No recorded statistics
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg shadow-lg bg-[#121212] p-4">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-[#CECFD2] border-b border-gray-700">
                        <th className="px-3 py-2">Competition</th>
                        <th className="px-3 py-2">Season</th>
                        <th className="px-3 py-2">Apps</th>
                        <th className="px-3 py-2">Goals</th>
                        <th className="px-3 py-2">Assists</th>
                        <th className="px-3 py-2">Minutes</th>
                        <th className="px-3 py-2">Yellow Cards</th>
                        <th className="px-3 py-2">Red Cards</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {Array.isArray(playerStats) && playerStats.map((stat, index) => (
                        <tr 
                          key={index} 
                          className="text-sm hover:bg-[#26222E] transition-colors duration-150"
                        >
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.competition_name}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.season_id}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.appearances}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.goals}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.assists}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">{stat.minutes_played}</td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">
                            {stat.yellow_cards > 0 && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-500/20 text-yellow-500 rounded-md">
                                {stat.yellow_cards}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium text-[#CECFD2]">
                            {stat.red_cards > 0 && (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-red-500/20 text-red-500 rounded-md">
                                {stat.red_cards}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      
      {/* Career Timeline Section */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        <h4 className="text-xl font-bold text-[#CECFD2] px-4 mb-4">Career Timeline</h4>
        <div className="w-full">
          <HorizontalCareerTimeline
            playerProfile={playerProfile}
            playerTransfers={playerTransfers}
            playerInjuries={playerInjuries}
            playerAchievements={playerAchievements}
            playerStats={playerStats}
          />
        </div>
      </div>
    </>
  );
};